import React, { Component } from 'react';
import Spinner from 'components/Spinner';
import ShowToast from 'components/ShowToast';
import moment from 'moment';
import { INTERVAL, TYPE_SUCCESS, TYPE_FAIL } from 'assets/constants/Constants';
import { ContactMailIcon, CALENDER_ICON } from 'assets/constants/Icons';
import { createCustomer, updateCustomer, createHistoryList, getMetaDataCall } from 'lib/axiosUtils';
import Select from 'react-select';
import { LOG_TEXT, MODULE_NAMES } from 'assets/constants/Constants';

const GST_TYPES = [
    'Regular',
    'Composition',
    'Unregistered',
    'Consumer',
    'SEZ',
    'Input Service Distributor',
    'Deemed',
    'Overseas',
];
const GST_REQUIRED = new Set([
    'Regular',
    'Composition',
    'SEZ',
    'Input Service Distributor',
    'Deemed',
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;
const PIN_RE = /^\d{6}$/;

export default class CreateCustomerPage extends Component {
    constructor(props) {
        super(props);

        const editPayload = props.location?.state || null;
        const prefill = editPayload?.prefill || null;

        this.state = {
            // page
            isLoading: true,
            isSaving: false,
            isEdit: !!prefill,
            id: editPayload?.customerId || prefill?._id || prefill?._id?.$oid || null,

            // form fields
            customer_name: prefill?.customer_name || '',
            full_address: prefill?.full_address || '',
            state_id: prefill?.state_id || '', 
            state_name: prefill?.state || '', 
            country: prefill?.country || 'India',
            pin_code: prefill?.pin_code || '',
            contact_name: prefill?.contact_name || '',
            email: prefill?.email || '',
            pan_no: prefill?.pan_no || '',
            gst_registration_type: prefill?.gst_registration_type || 'Unregistered',
            gst_no: prefill?.gst_no || '',
            vendor_code: prefill?.vendor_code || '',
            default_credit_period_days:
                typeof prefill?.default_credit_period_days === 'number'
                    ? prefill.default_credit_period_days
                    : '',
            temporary_customer: prefill?.temporary_customer || false,

            // errors
            errors: {},

            // metadata for states
            metaData: [], 
        };
    }
    componentDidMount = async () => {
        try {
            const response = await getMetaDataCall({}, {});
            if (response.data && response.data.result) {
                const result = response.data.result;
                const metaData = Array.isArray(result) ? result : [result];             
                this.setState({ metaData });
            }
        } catch (error) {
            console.error('Error fetching metadata:', error);
            ShowToast('Failed to load state list. Please refresh.', TYPE_FAIL, INTERVAL, this.props, '');
        } finally {
            this.setState({ isLoading: false });
        }
    };

    // ---------- helpers ----------
    setField = (key, val) => {
        this.setState((s) => ({
            ...s,
            [key]: val,
            errors: { ...s.errors, [key]: '' },
        }));
    };
    
    handleCountryChange = (e) => {
        const newCountry = e.target.value;
        const isOutsideIndia = newCountry?.trim().toLowerCase() !== 'india';

        if (isOutsideIndia) {
           
            this.setState((s) => ({
                ...s,
                country: newCountry,
                state_id: '',
                state_name: '',
                gst_registration_type: 'Overseas', 
                gst_no: '',
                errors: {
                    ...s.errors,
                    country: '',
                    state_name: '',
                    gst_registration_type: '',
                    gst_no: '',
                },
            }));
        } else {
            const newGstType =
                this.state.gst_registration_type === 'Overseas'
                    ? 'Unregistered'
                    : this.state.gst_registration_type;

            this.setState((s) => ({
                ...s,
                country: newCountry,
                gst_registration_type: newGstType,
                errors: { ...s.errors, country: '' },
            }));
        }
    };

    // ---------- validation ----------
    validate = () => {
        const {
            customer_name,
            state_name,
            country,
            pin_code,
            email,
            pan_no,
            gst_registration_type,
            gst_no,
            default_credit_period_days,
        } = this.state;

        const errors = {};
        const isOutsideIndia = country?.trim().toLowerCase() !== 'india'; 

        if (!customer_name?.trim()) errors.customer_name = 'Customer Name is required';    
        
        if (pin_code?.trim() && !PIN_RE.test(String(pin_code).trim())) {
            errors.pin_code = 'Enter a valid 6-digit PIN code';
        }
        if (email?.trim() && !EMAIL_RE.test(email.trim())) errors.email = 'Enter a valid email';
        if (pan_no?.trim() && !PAN_RE.test(pan_no.trim().toUpperCase()))
            errors.pan_no = 'PAN must be like ABCDE1234F';

        if (GST_REQUIRED.has(gst_registration_type)) {
            if (!gst_no?.trim())
                errors.gst_no = 'GST No. is required for the selected registration type';
            else if (!GSTIN_RE.test(gst_no.trim().toUpperCase()))
                errors.gst_no = 'Enter a valid 15-character GSTIN';
        } else if (gst_no?.trim() && !GSTIN_RE.test(gst_no.trim().toUpperCase())) {
            errors.gst_no = 'Enter a valid 15-character GSTIN';
        }

        if (
            default_credit_period_days !== '' &&
            (isNaN(default_credit_period_days) || Number(default_credit_period_days) < 0)
        ) {
            errors.default_credit_period_days = 'Credit period must be a non-negative number';
        }
        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };
    // ---------- payload ----------
    buildPayload = () => {
        const {
            customer_name,
            full_address,
            state_name, // send NAME
            // state_id, // keep if you also want to store id (optional)
            country,
            pin_code,
            contact_name,
            email,
            pan_no,
            gst_registration_type,
            gst_no,
            vendor_code,
            temporary_customer,
            default_credit_period_days,
        } = this.state;

        const payload = {
            customer_name: (customer_name || '').trim(),
            full_address: (full_address || '').trim(),
            state: (state_name || '').trim(), // IMPORTANT: send state NAME
            country: (country || '').trim(),
            pin_code: String(pin_code || '').trim(),
            contact_name: (contact_name || '').trim(),
            email: (email || '').trim(),
            pan_no: (pan_no || '').trim().toUpperCase(),
            gst_registration_type,
            gst_no: (gst_no || '').trim().toUpperCase(),
            vendor_code: vendor_code && vendor_code.trim() 
                ? vendor_code.trim().toUpperCase() 
                : undefined,
            temporary_customer: !!temporary_customer,
            default_credit_period_days:
                default_credit_period_days === '' ? 0 : Number(default_credit_period_days),
        };

        console.log('[CreateCustomerPage] payload:', payload);
        return payload;
    };

    // ---------- actions ----------
    onSave = async () => {
        if (!this.validate()) return;
        const payload = this.buildPayload();

        console.log('[CreateCustomerPage] saving customer...', payload);

        try {
            this.setState({ isSaving: true });

            if (this.state.isEdit && this.state.id) {
                // NOTE: typical signature is updateX(headers, id, body)
                await updateCustomer({}, payload, this.state.id);
                ShowToast(
                    'Customer updated successfully',
                    TYPE_SUCCESS,
                    INTERVAL,
                    this.props,
                    '/customer-list-page'
                );
            } else {
                let auditresponse=null;
                let custresponse=null;
                let userData = JSON.parse(localStorage.getItem('userData'));
                let userID = userData._id;
                let logText = LOG_TEXT.CUSTOMER_ADDED;
                let moduleName = MODULE_NAMES.CUSTOMER;
                custresponse = await createCustomer({}, payload);
                const customerData = custresponse.data.result;
                    const auditData = {
                        logText,
                        userID,
                        moduleName,
                        doc_id: customerData._id
                    };
                auditresponse = await createHistoryList({}, auditData);
                ShowToast(
                    'Customer created successfully',
                    TYPE_SUCCESS,
                    INTERVAL,
                    this.props,
                    '/customer-list-page'
                );
            }
        } catch (err) {
            console.error('[CreateCustomerPage] save failed:', err);
            const msg =
                err?.response?.data?.error ||
                err?.message ||
                'Something went wrong while saving the customer';
            ShowToast(msg, TYPE_FAIL, INTERVAL, this.props, '');
        } finally {
            this.setState({ isSaving: false });
        }
    };

    onCancel = () => {
        this.props.history.push('/customer-list-page');
    };

    // ---------- UI ----------
    renderFieldRow({ label, required = false, children, error }) {
        return (
            <div className="col-md-6 mb-3">
                <label className="form-label d-block">
                    {label} {required && <span className="text-danger">*</span>}
                </label>
                {children}
                {error && <div className="text-danger error-msg mt-1">{error}</div>}
            </div>
        );
    }

    render() {
        if (this.state.isLoading) return <Spinner />;

        const {
            isEdit,
            isSaving,
            customer_name,
            full_address,
            state_id,
            state_name,
            country,
            pin_code,
            contact_name,
            email,
            pan_no,
            gst_registration_type,
            gst_no,
            vendor_code,
            temporary_customer,
            default_credit_period_days,
            errors,
            metaData,
        } = this.state;

        // Build state options from metadata
        const isOutsideIndia = country?.trim().toLowerCase() !== 'india';
        const statesArray = metaData?.[0]?.meta_data?.states || [];
        const stateOptions = statesArray.map((s) => ({ value: s.state_id, label: s.state_name }));

        // pick currently selected option by either id OR name
        const selectedStateOption =
            stateOptions.find((opt) => opt.value === state_id) ||
            stateOptions.find((opt) => opt.label === state_name) ||
            null;

        const gstOptions = GST_TYPES.map((type) => ({ value: type, label: type }));

        return (
            <div className="main-content">
                <div className="pagetitle row" style={{ color: 'black', paddingTop: 15 , marginTop: '-25px'}}>
                    <div className="col-md-10 sm_device">
                        {isEdit ? (
                            <ContactMailIcon aria-hidden="true" />
                        ) : (
                            <ContactMailIcon aria-hidden="true" />
                        )}
                        <span style={{ marginLeft: '7px' }}>
                            {isEdit ? 'Edit Customer' : 'Create Customer'}
                        </span>
                    </div>
                    <div className="col-md-2 sm_device" style={{ textAlign: 'right' }}>
                        <i className={`${CALENDER_ICON}`}></i> {moment().format('DD MMM YYYY')}
                    </div>
                </div>

                <div
                    style={{ borderBottom: '1px solid lightgray', marginBottom: 15, marginTop: 15 }}
                />

                <div className="list-wrapper">
                    <div
                        className="mt-4 border list-table-div"
                        style={{ marginBottom: 50, padding: 20 }}
                    >
                        <div className="row">
                            {this.renderFieldRow({
                                label: 'Customer Name',
                                required: true,
                                children: (
                                    <input
                                        className="form-control-customer"
                                        value={customer_name}
                                        onChange={(e) =>
                                            this.setField('customer_name', e.target.value)
                                        }
                                        placeholder="Enter Customer Name"
                                    />
                                ),
                                error: errors.customer_name,
                            })}
                            {this.renderFieldRow({
                                label: 'Contact Name',
                                required: true,
                                children: (
                                    <input
                                        className="form-control-customer"
                                        value={contact_name}
                                        onChange={(e) =>
                                            this.setField('contact_name', e.target.value)
                                        }
                                        placeholder="Enter Contact Name"
                                    />
                                ),
                                error: errors.contact_name,
                            })}
                        </div>

                        <div className="row">
                            {this.renderFieldRow({
                                label: 'E-Mail ID',
                                required: true,
                                children: (
                                    <input
                                        className="form-control-customer"
                                        value={email}
                                        onChange={(e) => this.setField('email', e.target.value)}
                                        placeholder="name@company.com"
                                    />
                                ),
                                error: errors.email,
                            })}
                            {this.renderFieldRow({
                                label: 'PAN No.',
                                required: !isOutsideIndia,
                                children: (
                                    <input
                                        className="form-control-customer"
                                        value={pan_no}
                                        onChange={(e) =>
                                            this.setField('pan_no', e.target.value.toUpperCase())
                                        }
                                        placeholder="ABCDE1234F"
                                        maxLength={10}
                                    />
                                ),
                                error: errors.pan_no,
                            })}
                        </div>

                        <div className="row">
                            {this.renderFieldRow({
                                label: 'Country',
                                required: true,
                                children: (
                                    <input
                                        className="form-control-customer"
                                        value={country}
                                        onChange={this.handleCountryChange}
                                        placeholder="Enter Country"
                                    />
                                ),
                                error: errors.country,
                            })}

                            {this.renderFieldRow({
                                label: (
                                    <span
                                        style={{
                                            color: isOutsideIndia ? '#9e9e9e' : 'inherit', 
                                        }}
                                    >
                                        State
                                    </span>
                                ),
                                required: !isOutsideIndia,
                                children: (
                                    <Select
                                        value={selectedStateOption}
                                        onChange={(selected) =>
                                            this.setState({
                                                state_id: selected ? selected.value : '',
                                                state_name: selected ? selected.label : '',
                                                errors: { ...this.state.errors, state_name: '' },
                                            })
                                        }
                                        options={stateOptions}
                                        placeholder="Select State"
                                        isClearable
                                        isDisabled={isOutsideIndia}
                                    />
                                ),
                                error: errors.state_name,
                            })}
                        </div>

                        <div className="row">
                            {this.renderFieldRow({
                                label: 'Address',
                                children: (
                                    <textarea
                                        rows={2}
                                        className="form-control-customer"
                                        value={full_address}
                                        onChange={(e) =>
                                            this.setField('full_address', e.target.value)
                                        }
                                        placeholder="Enter full address"
                                    />
                                ),
                                error: errors.full_address,
                            })}

                            {this.renderFieldRow({
                                label: 'PIN Code',
                                required: false, 
                                children: (
                                    <input
                                        className="form-control-customer"
                                        value={pin_code}
                                        onChange={(e) => this.setField('pin_code', e.target.value)}
                                        placeholder="6-digit PIN"
                                        maxLength={6}
                                    />
                                ),
                                error: errors.pin_code,
                            })}
                        </div>


                        <div className="row">
                            {this.renderFieldRow({
                                label: (
                                    <span
                                        style={{
                                            color: isOutsideIndia ? '#9e9e9e' : 'inherit', 
                                        }}
                                    >
                                        GST Registration Type
                                    </span>
                                ),
                                required: true,
                                children: (
                                    <Select
                                        value={
                                            gstOptions.find(
                                                (opt) =>
                                                    opt.value === this.state.gst_registration_type
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                            this.setState({
                                                gst_registration_type: selected
                                                    ? selected.value
                                                    : 'Unregistered',
                                                errors: {
                                                    ...this.state.errors,
                                                    gst_registration_type: '',
                                                    gst_no: '',
                                                },
                                            })
                                        }
                                        options={gstOptions}
                                        placeholder="Select GST Type"
                                        isClearable
                                        isDisabled={isOutsideIndia}
                                    />
                                ),
                                error: errors.gst_registration_type,
                            })}

                            {this.renderFieldRow({
                                label: (
                                    <span
                                        style={{
                                            color: isOutsideIndia ? '#9e9e9e' : 'inherit', 
                                        }}
                                    >
                                        GST No.
                                    </span>
                                ),
                                required: GST_REQUIRED.has(gst_registration_type) && !isOutsideIndia,
                                disabled: isOutsideIndia,
                                children: (
                                    <input
                                        className="form-control-customer"
                                        style={{
                                            backgroundColor: isOutsideIndia ? '#f0f0f0' : 'white',
                                            color: isOutsideIndia ? '#9e9e9e' : 'black',
                                            cursor: isOutsideIndia ? 'not-allowed' : 'text',
                                        }}
                                        value={gst_no}
                                        onChange={(e) =>
                                            this.setField('gst_no', e.target.value.toUpperCase())
                                        }
                                        placeholder="e.g 27ABCDE1234F1Z5"
                                        maxLength={15}
                                        disabled={isOutsideIndia}
                                    />
                                ),
                                error: errors.gst_no,
                            })}
                        </div>

                        <div className="row">
                            {this.renderFieldRow({
                                label: 'Vendor Code',
                                required: false,
                                children: (
                                    <input
                                        className="form-control-customer"
                                        value={vendor_code}
                                        onChange={(e) =>
                                            this.setField(
                                                'vendor_code',
                                                e.target.value.toUpperCase()
                                            )
                                        }
                                        placeholder="Your internal vendor code"
                                    />
                                ),
                                error: errors.vendor_code,
                            })}

                            {this.renderFieldRow({
                                label: 'Default Credit Period (days)',
                                required: true,
                                children: (
                                    <input
                                        type="number"
                                        min={0}
                                        className="form-control-customer"
                                        value={default_credit_period_days}
                                        onChange={(e) =>
                                            this.setField(
                                                'default_credit_period_days',
                                                e.target.value
                                            )
                                        }
                                        placeholder="e.g., 30"
                                    />
                                ),
                                error: errors.default_credit_period_days,
                            })}
                        </div>
                        
                        {/* <div className="row">
                            {this.renderFieldRow({
                                label: 'Temporary Customer',
                                required: false,
                                children: (
                                    <div className="mt-2" style={{ display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={temporary_customer}
                                            onChange={(e) =>
                                                this.setField('temporary_customer', e.target.checked)
                                            }
                                            id="temporary_customer"
                                            style={{ cursor: 'pointer', width: '20px', height: '20px', marginRight: '10px' }}
                                        />
                                        <label htmlFor="temporary_customer" style={{ cursor: 'pointer', margin: 0 }}>
                                            Yes
                                        </label>
                                    </div>
                                ),
                                error: errors.temporary_customer,
                            })}
                        </div> */}

                        <div className="row mt-4 mb-3">
                            <div className="col-md-8">
                                <button
                                    className="brand-button"
                                    onClick={this.onSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Save'}
                                </button>
                                <button
                                    className="cancel_button ml-3"
                                    onClick={this.onCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>

                        {isSaving && <Spinner />}
                    </div>
                </div>
            </div>
        );
    }
}
